import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  updateCategoryByIdLanguage,
  viewCategoryLanguageById,
} from '../../services/content.service';
import TranslationSidebar from '../common/translation-sidebar.component';
import TranslationEditor from '../common/translation-editor.component';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const TRANSLATABLE_FIELDS = [
  {
    key: 'title',
    label: 'Book Name',
    type: 'text',
  },
  {
    key: 'long_description',
    label: 'Description',
    type: 'textarea',
  },
];

const TranslateBooks = ({ title, dataId, onCancel, onSuccess, rowData }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm();

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  const onSelectLanguage = (lang) => {
    setSelectedLanguage(lang);
  };

  const updateExistingData = (payload) => {
    updateCategoryByIdLanguage(dataId, payload)
      .then(() => {
        onSuccess(`Translation details updated successfully.`);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {};
    for (let i = 0; i < languages.length; i += 1) {
      payload[`title_${languages[i].id}`] = data[`title_${languages[i].value}`]
        .replace(/\s+/g, ' ')
        .trim();
      payload[`description_${languages[i].id}`] = data[
        `long_description_${languages[i].value}`
      ]
        .replace(/\s+/g, ' ')
        .trim();
    }

    if (dataId) {
      updateExistingData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }

    viewCategoryLanguageById(dataId)
      .then((res) => {
        setLanguages(res.data.languages || []);
        setSelectedLanguage(res.data.languages?.[0]?.value || null);

        // prefill translation form values
        for (let i = 0; i < res.data.languages.length; i += 1) {
          const langCode = res.data.languages[i].value;
          const langData = res.data.categoryLang?.filter(
            (item) => item.languageCode === langCode
          );

          setValue(`title_${langCode}`, langData[0]?.title || '');
          setValue(
            `long_description_${langCode}`,
            langData[0]?.description || ''
          );
        }

        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: '80vh',
          maxWidth: '1400px',
          m: 2,
        },
      }}
    >
      <DialogTitle
        id="view-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Iconify icon="ic:round-translate" />
          <Box>
            <Typography variant="h6" component="span">
              {title}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          size="small"
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Iconify icon="ic:outline-close" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers id="add-dialog-description" sx={{ p: 0 }}>
        {loading && <Loading />}

        {!loading && (
          <Box sx={{ display: 'flex', height: '100%' }}>
            <TranslationSidebar
              languages={languages}
              selectedLanguage={selectedLanguage}
              onSelectLanguage={onSelectLanguage}
            />

            {selectedLanguage ? (
              <form
                id="add-edit-form"
                onSubmit={handleSubmit(onFormSubmit)}
                style={{ flex: 1 }}
              >
                <TranslationEditor
                  key={selectedLanguage}
                  languages={languages}
                  selectedLanguage={selectedLanguage}
                  control={control}
                  errors={errors}
                  rowData={rowData}
                  translationFields={TRANSLATABLE_FIELDS}
                />
              </form>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Iconify
                    icon="ic:round-translate"
                    sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Select a Language
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a language from the sidebar to start translating
                    content.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ marginRight: 2 }}>
        <Button
          color="inherit"
          variant="outlined"
          onClick={onCancel}
          disabled={formSubmitted}
          sx={{ borderRadius: '10px' }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-edit-form"
          color="primary"
          variant="contained"
          disabled={loading || formSubmitted}
          sx={{
            borderRadius: '10px',
            boxShadow: (theme) => theme.customShadows.primary,
          }}
        >
          Save
        </Button>
      </DialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

TranslateBooks.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  rowData: PropTypes.object.isRequired,
};

export default TranslateBooks;
